import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, TrendingUp, CheckSquare, FileSignature, CalendarDays, MessageSquare,
  Phone, Bell, GraduationCap, Megaphone, Receipt, LifeBuoy, ArrowRight,
} from 'lucide-react';
import { fadeUp, staggerParent, viewportOnce } from './motionPresets';

// Demo-only mock data — no real client information.
const TABS = [
  {
    id: 'clients',
    label: 'Clients',
    icon: Users,
    render: () => (
      <div className="space-y-2.5" data-testid="hub-panel-clients">
        {[
          { n: 'A. Martinez', p: 'Medicare Advantage', s: 'Active', t: 'text-emerald-300 bg-emerald-400/15' },
          { n: 'J. Thompson', p: 'ACA — Family', s: 'Renewal', t: 'text-amber-300 bg-amber-400/15' },
          { n: 'R. Chen', p: 'Term Life', s: 'Active', t: 'text-emerald-300 bg-emerald-400/15' },
          { n: 'D. Okafor', p: 'Dental + Vision', s: 'New', t: 'text-sky-300 bg-sky-400/15' },
        ].map((r) => (
          <div key={r.n} className="flex items-center gap-3 rounded-lg bg-white/[0.04] border border-white/5 px-3 py-2.5">
            <span className="w-8 h-8 rounded-full bg-[#1a3a7a] text-[11px] font-bold flex items-center justify-center text-white">{r.n.split(' ')[1][0]}</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-white">{r.n}</div>
              <div className="text-[11px] text-slate-400">{r.p}</div>
            </div>
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${r.t}`}>{r.s}</span>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: 'leads',
    label: 'Leads',
    icon: TrendingUp,
    render: () => (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="hub-panel-leads">
        {[
          { c: 'New', v: 9, tone: 'from-sky-500/30' },
          { c: 'Working', v: 14, tone: 'from-indigo-500/30' },
          { c: 'Quoted', v: 6, tone: 'from-amber-500/30' },
          { c: 'Won', v: 4, tone: 'from-emerald-500/30' },
        ].map((col) => (
          <div key={col.c} className={`rounded-lg bg-gradient-to-b ${col.tone} to-transparent border border-white/10 p-2.5`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-300">{col.c}</div>
            <div className="text-2xl font-bold text-white mt-1">{col.v}</div>
            <div className="mt-2 space-y-1">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="h-1.5 rounded bg-white/15" />
              ))}
            </div>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: 'contracting',
    label: 'Contracting',
    icon: FileSignature,
    render: () => (
      <div className="space-y-2.5" data-testid="hub-panel-contracting">
        {[
          { c: 'Aetna', step: 'Ready to Sell', pct: 100 },
          { c: 'UnitedHealthcare', step: 'Background review', pct: 70 },
          { c: 'Humana', step: 'Submitted', pct: 40 },
          { c: 'Cigna', step: 'Awaiting docs', pct: 20 },
        ].map((r) => (
          <div key={r.c} className="rounded-lg bg-white/[0.04] border border-white/5 px-3 py-2.5">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-white">{r.c}</span>
              <span className="text-[11px] text-slate-400">{r.step}</span>
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-[#3b82f6] to-[#60a5fa]" style={{ width: `${r.pct}%` }} />
            </div>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: 'calendar',
    label: 'Calendar',
    icon: CalendarDays,
    render: () => (
      <div className="space-y-2.5" data-testid="hub-panel-calendar">
        {[
          { t: '9:00 AM', e: 'Client review — A. Martinez', tone: 'border-l-[#3b82f6]' },
          { t: '11:30 AM', e: 'Carrier webinar — Humana', tone: 'border-l-[#b8862d]' },
          { t: '2:00 PM', e: 'Team standup', tone: 'border-l-emerald-400' },
          { t: '4:15 PM', e: 'Follow-up calls (5)', tone: 'border-l-sky-400' },
        ].map((r) => (
          <div key={r.e} className={`flex items-center gap-3 rounded-lg bg-white/[0.04] border border-white/5 border-l-2 ${r.tone} px-3 py-2.5`}>
            <span className="text-xs font-mono text-slate-300 w-16 shrink-0">{r.t}</span>
            <span className="text-sm text-white">{r.e}</span>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: 'messaging',
    label: 'Text & Calls',
    icon: MessageSquare,
    render: () => (
      <div className="space-y-3" data-testid="hub-panel-messaging">
        <div className="flex justify-start">
          <div className="max-w-[75%] rounded-2xl rounded-tl-sm bg-white/10 px-3.5 py-2 text-sm text-slate-100">Hi! Is my plan active for January?</div>
        </div>
        <div className="flex justify-end">
          <div className="max-w-[75%] rounded-2xl rounded-tr-sm bg-[#3b82f6] px-3.5 py-2 text-sm text-white">Yes — confirmed effective 01/01. I'll text your member ID shortly.</div>
        </div>
        <div className="flex items-center gap-2 rounded-full bg-white/[0.06] border border-white/10 px-3 py-2">
          <span className="text-xs text-slate-400 flex-1">Type a message…</span>
          <Phone size={14} className="text-emerald-300" />
          <MessageSquare size={14} className="text-[#f5c876]" />
        </div>
      </div>
    ),
  },
  {
    id: 'statements',
    label: 'Statements',
    icon: Receipt,
    render: () => (
      <div data-testid="hub-panel-statements">
        <div className="flex items-end gap-2 h-28 px-1">
          {[40, 55, 48, 70, 62, 85].map((h, i) => (
            <div key={i} className="flex-1 rounded-t-md bg-gradient-to-t from-[#b8862d] to-[#f5c876]" style={{ height: `${h}%` }} />
          ))}
        </div>
        <div className="mt-2 flex justify-between text-[10px] text-slate-400 px-1">
          {['Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'].map((m) => <span key={m}>{m}</span>)}
        </div>
        <p className="mt-3 text-xs text-slate-400">Payout history and statements — visible where authorized.</p>
      </div>
    ),
  },
];

const MODULES = [
  { icon: Users, label: 'Clients' },
  { icon: TrendingUp, label: 'Leads' },
  { icon: CheckSquare, label: 'Follow-ups' },
  { icon: FileSignature, label: 'Contracting' },
  { icon: CalendarDays, label: 'Calendar' },
  { icon: MessageSquare, label: 'Text & Calls' },
  { icon: Megaphone, label: 'Marketing' },
  { icon: GraduationCap, label: 'Training' },
  { icon: Bell, label: 'Notifications' },
  { icon: Receipt, label: 'Statements' },
  { icon: LifeBuoy, label: 'Support' },
];

export default function AgencyHubShowcase() {
  const [active, setActive] = useState('clients');
  const tab = TABS.find((t) => t.id === active);

  return (
    <section id="agency-hub" className="scroll-mt-28 lg:scroll-mt-32 relative overflow-hidden bg-[#071533] text-white py-20 sm:py-28 lg:py-32" data-testid="agency-hub-section">
      <div className="absolute top-0 right-0 w-[40rem] h-[40rem] rounded-full bg-[#1a3a7a]/40 blur-[140px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Copy */}
          <motion.div variants={staggerParent} initial="hidden" whileInView="show" viewport={viewportOnce} className="lg:col-span-5">
            <motion.span variants={fadeUp} className="text-xs font-semibold uppercase tracking-widest text-[#f5c876]">Your business. All in one place.</motion.span>
            <motion.h2 variants={fadeUp} className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">The KR Agency Hub.</motion.h2>
            <motion.p variants={fadeUp} className="mt-5 text-lg text-slate-300 leading-relaxed">
              KR built its own infrastructure for its agents. Manage clients, leads, contracting, communications,
              training, and marketing — from one modern platform, anywhere.
            </motion.p>
            <motion.div variants={fadeUp} className="mt-6 flex flex-wrap gap-2">
              {MODULES.map((m) => (
                <span key={m.label} className="inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-white/10 px-3 py-1.5 text-xs text-slate-200">
                  <m.icon size={13} className="text-[#60a5fa]" /> {m.label}
                </span>
              ))}
            </motion.div>
          </motion.div>

          {/* Interactive command-center preview */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} className="lg:col-span-7">
            <div className="rounded-2xl border border-white/10 bg-[#0b1f45]/70 backdrop-blur-xl shadow-2xl overflow-hidden">
              <div className="flex items-center gap-1.5 px-4 py-3 border-b border-white/10">
                <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
                <span className="ml-3 text-[11px] text-slate-400 font-mono">app.krenterprisegroup — Agency Hub</span>
              </div>
              {/* Tab bar */}
              <div className="flex flex-wrap gap-1.5 px-3 pt-3">
                {TABS.map((t) => {
                  const on = t.id === active;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setActive(t.id)}
                      data-testid={`agency-hub-module-tab-${t.id}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        on ? 'bg-white/10 text-white border border-white/15' : 'text-slate-400 hover:text-white hover:bg-white/5 border border-transparent'
                      }`}
                    >
                      <t.icon size={13} className={on ? 'text-[#f5c876]' : ''} /> {t.label}
                    </button>
                  );
                })}
              </div>
              {/* Panel */}
              <div className="p-4 min-h-[15rem]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={active}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.28, ease: 'easeOut' }}
                  >
                    {tab.render()}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
            <p className="mt-3 text-center text-[11px] text-slate-500">Interface preview for illustration. No real client data shown.</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
